import { useCallback, useEffect } from 'react'

import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useLazyGetContactSchemaQuery } from '../../../store/integrations/integrations'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  clearDocumentProspectingDetailsForm,
  resetDocumentProspecting,
  selectDocumentProspectingCurrentUser,
  selectDocumentProspectingDetailsFormProps,
  selectDocumentProspectingDetailsFormPropsLoading,
  selectDocumentProspectingAttachment,
  selectDocumentProspectingServiceId,
  setDocumentProspectingCurrentUser,
  setDocumentProspectingCurrentUserIsLoading,
  setDocumentProspectingDetailsFormProps,
  setDocumentProspectingDetailsFormPropsLoading,
  setDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'

import {
  DOCUMENT_CONTACT_UPDATE_SUPPORTED_INTEGRATIONS,
  INTEGRATIONS_WITH_DYNAMIC_SCHEMA,
  INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE,
  IntegrationServiceId,
} from '../../../common/integrations'

import { AccountIntegrationConfigMappingType } from '../../../models/Account'

import { useProspectingDetailsForm } from '../DocumentProspecting/hooks'

import { useInitializeDocumentTypes } from './hooks'
import { getAccountIntegrationConfigMappings } from './utils'
import { useLazyGetMeQuery } from '../../../store/user/user'

export const DocumentProspectingInitialization = () => {
  useInitializeDocumentTypes()
  useInitializeDocumentProspecting()
  useInitializeDocumentProspectingUsers()

  return null
}

const useInitializeDocumentProspecting = () => {
  const dispatch = useAppDispatch()

  const currentUser = useAppSelector(selectDocumentProspectingCurrentUser)

  useInitializeDocumentProspectingDetailsForm()

  useEffect(() => {
    if (!currentUser) {
      return
    }

    const integrations =
      currentUser.integrations?.filter(({ serviceId }) => DOCUMENT_CONTACT_UPDATE_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId)) ?? []

    if (integrations.length === 1) {
      const { serviceId } = integrations[0]
      dispatch(setDocumentProspectingServiceId(serviceId as IntegrationServiceId))
    }
  }, [currentUser, dispatch])

  useEffect(() => {
    return () => {
      dispatch(resetDocumentProspecting())
    }
  }, [dispatch])
}

const useInitializeDocumentProspectingDetailsForm = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const attachment = useAppSelector(selectDocumentProspectingAttachment)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)

  const detailsFormProps = useAppSelector(selectDocumentProspectingDetailsFormProps)
  const detailsFormPropsLoading = useAppSelector(selectDocumentProspectingDetailsFormPropsLoading)

  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const [triggerGetIntegrationContactSchema] = useLazyGetContactSchemaQuery()

  const { generateFormValues } = useProspectingDetailsForm()

  const initializeDetailsFormForIntegrationWithStaticSchema = useCallback(
    async (serviceId: IntegrationServiceId, contactObjectName: string) => {
      dispatch(setDocumentProspectingDetailsFormPropsLoading(true))
      dispatch(clearDocumentProspectingDetailsForm())
      try {
        const { data: integrationContactSchemaResponse } = await triggerGetIntegrationContactSchema({
          accountId,
          connector: serviceId,
          context: { objectName: contactObjectName },
        })
        if (!integrationContactSchemaResponse) {
          return
        }
        const { data: schema } = integrationContactSchemaResponse
        const previousValues = {}
        const propsType = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId) ? 'segmented' : 'default'
        dispatch(setDocumentProspectingDetailsFormProps({ type: propsType, schema, previousValues }))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentProspectingDetailsFormPropsLoading(false))
      }
    },
    [accountId, dispatch, triggerGetIntegrationContactSchema],
  )

  useEffect(() => {
    if (!serviceId || !accountResponse?.data || !attachment) {
      return
    }

    if (INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId)) {
      return
    }

    const { data: account } = accountResponse
    const accountIntegrationConfigMappings = getAccountIntegrationConfigMappings(account, serviceId)
    const contactObjectName = accountIntegrationConfigMappings[AccountIntegrationConfigMappingType.CONTACT]

    initializeDetailsFormForIntegrationWithStaticSchema(serviceId, contactObjectName)
  }, [accountResponse, attachment, initializeDetailsFormForIntegrationWithStaticSchema, serviceId])

  useEffect(() => {
    if (!serviceId || !attachment || !detailsFormProps || detailsFormPropsLoading) {
      return
    }

    generateFormValues(serviceId, attachment, detailsFormProps)
  }, [attachment, detailsFormProps, detailsFormPropsLoading, generateFormValues, serviceId])
}

const useInitializeDocumentProspectingUsers = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetMe] = useLazyGetMeQuery()

  useEffect(() => {
    const fetchMe = async () => {
      dispatch(setDocumentProspectingCurrentUserIsLoading(true))
      try {
        const { data: currentUser } = await triggerGetMe({ accountId }, true)
        if (!currentUser) {
          return
        }
        dispatch(setDocumentProspectingCurrentUser(currentUser))
      } finally {
        dispatch(setDocumentProspectingCurrentUserIsLoading(false))
      }
    }

    fetchMe()
  }, [dispatch, accountId, triggerGetMe])
}
